import axios from 'axios'
import toast from 'react-hot-toast'
import { bffBaseURL, normalizeError, getCsrfToken, setCsrfToken } from './apiClient'

// ============================================================================
// Authentication API — Phase 34 Cookie-Based Auth
// ============================================================================

export async function login(credentials: { username: string; password: string }) {
  try {
    console.log('Attempting login with cookie-based authentication')

    const response = await axios.post(
      `${bffBaseURL}/api/session/login`,
      credentials,
      {
        withCredentials: true
      }
    )

    if (!response.data || !response.data.user) {
      console.error('Login response missing user data:', response.data)
      throw new Error('Invalid login response from server')
    }

    const csrfHeader = response.headers['x-csrf-token'] || response.headers['X-CSRF-Token']
    if (csrfHeader) {
      setCsrfToken(csrfHeader)
    } else if (response.data?.csrf_token) {
      setCsrfToken(response.data.csrf_token)
    }

    console.log('Login successful for user:', response.data.user.username)

    return {
      user: response.data.user,
      sessionManagement: 'cookie-only',
      csrfToken: getCsrfToken()
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      let message = 'Login failed'

      if (error.response?.status === 401) {
        message = (error.response?.data as any)?.detail || 'Invalid credentials'
      } else if (error.response?.status === 423) {
        message = (error.response?.data as any)?.detail || 'No se pudo iniciar sesión. Su cuenta está bloqueada temporalmente.'
      } else if (error.response?.status === 429) {
        message = 'Too many login attempts. Please try again later.'
      } else if (error.response?.status && error.response.status >= 500) {
        message = 'Server error. Please try again later.'
      } else if (!error.response) {
        message = 'Unable to connect to server. Please check your connection.'
      } else {
        message = (error.response?.data as any)?.detail || message
      }

      const formattedMessage = normalizeError(message)
      console.error('Login failed:', formattedMessage)
      toast.error(formattedMessage)
      throw new Error(formattedMessage)
    }
    throw error
  }
}

export async function validateSession() {
  try {
    const response = await axios.get(`${bffBaseURL}/api/session/validate`, {
      skipTokenRefresh: true,
      withCredentials: true
    } as any)

    const csrfHeader = response.headers['x-csrf-token'] || response.headers['X-CSRF-Token']
    if (csrfHeader) {
      setCsrfToken(csrfHeader)
    }

    if (response.data && response.data.valid) {
      console.log('Session validation successful for user:', response.data.user?.username)
      return response.data
    } else {
      console.log('Session validation returned invalid session')
      throw new Error('Session is not valid')
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        console.log('Session validation failed - 401 Unauthorized')
        throw new Error('Session expired or invalid')
      } else if (error.response?.status === 403) {
        console.log('Session validation failed - 403 Forbidden')
        throw new Error('Access denied')
      } else if (!error.response) {
        console.log('Session validation failed - Network error')
        throw new Error('Unable to validate session - network error')
      } else {
        const message = normalizeError((error.response?.data as any)?.detail || 'Session validation failed')
        console.log('Session validation failed:', message)
        throw new Error(message)
      }
    }
    throw error
  }
}

export async function destroySession() {
  try {
    const token = getCsrfToken()
    await axios.delete(`${bffBaseURL}/api/session/destroy`, {
      skipTokenRefresh: true,
      withCredentials: true,
      headers: token ? { 'X-CSRF-Token': token } : {}
    } as any)
    setCsrfToken(null)
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Session destruction failed')
      throw new Error(message)
    }
    throw error
  }
}

export async function logout() {
  try {
    await destroySession()
    console.log('Logout completed - cookies cleared by BFF')
  } catch {
    console.warn('Session destruction failed during logout - cookies may still be cleared')
  }
}

export async function changePassword(passwordData: any) {
  try {
    const response = await axios.post(`${bffBaseURL}/api/profile/change-password`, passwordData, {
      skipTokenRefresh: true,
      withCredentials: true
    } as any)

    console.log('Password change successful')
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Password change failed')
      throw new Error(message)
    }
    throw error
  }
}

// W2/BUG-121-C1 (Phase 121, 2026-07-09): `validatePasswordRequirements()` and
// `getProfile()` deleted. Both bypassed the BFF via a hardcoded auth-service URL,
// were dead code (zero callers / unmounted hook), and could never authenticate from
// a browser anyway (auth-service profile endpoints are HTTPBearer, not cookie-based).
// If session-health polling or password pre-validation is ever wanted, build it as
// BFF proxy routes per Phase 96's change-password pattern — do not call auth-service
// directly. History: Phases 25 (origin), 41/85 (flagged), 96 (fixed change-password).
